import type { DismissReason, ProposalTool } from './investigation'

export type LearningKind = 'known_fix' | 'client_context'
export type LearningScope = 'global' | 'company'

export interface ConfirmationCheck {
  tool: string
  args: Record<string, unknown>
  expectation: string
}

export interface FixAction {
  tool: ProposalTool
  args: Record<string, unknown>
  summary: string
}

export interface LearningStats {
  matchedCount: number
  lastMatchedAt: string | null
  appliedCount: number
  dismissedCount: number
  dismissReasons: Partial<Record<DismissReason | 'none', number>>
}

export interface LearningFields {
  kind: LearningKind
  scope: LearningScope
  companyId: string | null
  title: string
  symptoms: string
  confirmationChecks: ConfirmationCheck[]
  fixActions: FixAction[]
  replyTemplate: string
  notes: string
  body: string
}

export interface LearningVersion {
  version: number
  snapshot: LearningFields
  editedBy: string | null
  editedAt: string
}

export interface Learning extends LearningFields {
  _id: string
  active: boolean
  ownerId: string
  version: number
  stats: LearningStats
  needsReview: boolean
  history?: LearningVersion[]
  sourceInvestigationId?: string | null
  createdAt: string
  updatedAt: string
}

// The reads a confirmation check may use (the Support Agent's read tools)
export const CHECK_TOOLS = [
  'search_accounts',
  'get_account',
  'search_assets',
  'get_asset',
  'search_entities',
  'search_invoices',
  'get_invoice',
  'search_invoice_captures',
  'search_invoice_validations',
  'get_bill_validation',
  'get_invoice_text',
  'search_readings',
  'search_consumptions',
  'search_tasks',
  'search_users',
  'search_roles',
  'search_permissions',
  'search_activity_logs',
  'search_suppliers',
  'search_support_tickets',
  'get_support_ticket',
  'global_search',
  'search_automations',
  'search_automation_runs',
  'search_documents',
  'search_knowledge_base'
] as const

export const FIX_TOOLS: ProposalTool[] = [
  'update_account',
  'update_account_status',
  'update_asset',
  'update_entity',
  'update_supplier',
  'update_task',
  'recapture_invoice',
  'enable_data_collection'
]

export const EMPTY_LEARNING: LearningFields = {
  kind: 'known_fix',
  scope: 'global',
  companyId: null,
  title: '',
  symptoms: '',
  confirmationChecks: [],
  fixActions: [],
  replyTemplate: '',
  notes: '',
  body: ''
}

// Only the fields a version holds: an edit must never send the history, stats or metadata back
export function versionedFields(source: LearningFields): LearningFields {
  return {
    kind: source.kind,
    scope: source.scope,
    companyId: source.companyId,
    title: source.title,
    symptoms: source.symptoms,
    confirmationChecks: source.confirmationChecks,
    fixActions: source.fixActions,
    replyTemplate: source.replyTemplate,
    notes: source.notes,
    body: source.body
  }
}
